<!-- 商品管理 V2 详情 - 购买渠道区块 -->
<template>
  <section class="purchase-shell">
    <h2>购买渠道</h2>
    <!-- 线上 / 线下：切换后立即保存 buy_channel 字段 -->
    <div class="channel-radio">
      <el-radio v-model="detail.buy_channel.buy_channel" :label="1" @change="saveField('buy_channel')">线上</el-radio>
      <el-radio v-model="detail.buy_channel.buy_channel" :label="2" @change="saveField('buy_channel')">线下</el-radio>
    </div>
    <div class="purchase-grid">
      <!-- 付款模式：全款不展示比例；分批付款只允许编辑首款比例 -->
      <article class="purchase-card">
        <h3>付款模式</h3>
        <el-radio v-model="detail.buy_channel.pay_type" :label="1" @change="handlePayTypeChange">全款支付</el-radio>
        <el-radio v-model="detail.buy_channel.pay_type" :label="2" @change="handlePayTypeChange">分批付款</el-radio>
        <!-- 比例值直接使用详情接口返回值，只有用户输入首款时才重新计算尾款 -->
        <div v-if="detail.buy_channel.pay_type == 2" class="rate-row">
          <label>首款比例<el-input v-model="detail.buy_channel.part_pay_first_rate" type="number" min="0" max="100" step="1" @keydown.native="preventInvalidRateKey" @paste.native="preventInvalidRatePaste" @input="handleFirstRateInput" @blur="savePaymentRates"><template slot="append">%</template></el-input></label>
          <label>尾款比例<el-input v-model="detail.buy_channel.part_pay_last_rate" type="number" min="0" max="100" readonly @keydown.native.prevent @paste.native.prevent><template slot="append">%</template></el-input></label>
        </div>
      </article>
      <!-- 合同文件：展示当前文件，支持上传和删除并分别保存 URL 与文件名 -->
      <article class="purchase-card contract-card">
        <h3>合同文件</h3>
        <div v-if="detail.buy_channel.file" class="contract-file">
          <a :href="detail.buy_channel.file" target="_blank" rel="noopener noreferrer">{{ detail.buy_channel.file_name }}</a>
          <button type="button" class="delete-contract" title="删除" @click="deleteContract"><i class="el-icon-delete"></i></button>
        </div>
        <el-upload class="contract-upload" action="#" :show-file-list="false" :http-request="uploadContract"><el-button class="blue-button">文件上传</el-button></el-upload>
      </article>
      <!-- 联系人：下拉选择或输入失焦后保存对应字段 -->
      <article class="purchase-card contact-card">
        <h3>联系人</h3>
        <label>业务联系人：<el-select v-model="detail.buy_channel.y_id" filterable placeholder="请选择" @change="saveField('y_id')"><el-option v-for="item in authWorkGroupAdmin.y" :key="item.id" :label="item.realname" :value="item.id" /></el-select></label>
        <label>采购联系人：<el-select v-model="detail.buy_channel.g_id" filterable placeholder="请选择" @change="saveField('g_id')"><el-option v-for="item in authWorkGroupAdmin.g" :key="item.id" :label="item.realname" :value="item.id" /></el-select></label>
        <label>工厂联系人：<el-input v-model="detail.buy_channel.factory_contact_name" placeholder="联系人名字" @blur="saveField('factory_contact_name')" /><el-input v-model="detail.buy_channel.factory_contact_mobile" placeholder="联系人手机号" @blur="saveField('factory_contact_mobile')" /></label>
      </article>
    </div>
  </section>
</template>

<script>
import { mapState } from 'vuex'

/**
 * 购买渠道编辑
 * 字段变更时调用 setBuyChannel 单字段保存
 */
export default {
  name: 'BuyChannelEditor',
  props: {
    detail: { type: Object, required: true }
  },
  computed: {
    ...mapState(['authWorkGroupAdmin'])
  },
  methods: {
    // 使用详情接口要求的 user_id、sys_code、column、value 保存单个购买渠道字段。
    saveField(column) {
      return this.$api.productManagementApi.setBuyChannel({
        user_id: this.detail.user_id,
        sys_code: this.detail.sys_code,
        column,
        value: this.detail.buy_channel[column]
      })
    },
    normalizeRate(value) {
      // 首款只保留数字并限制在 0-100；空值保持空，不补默认比例。
      const text = String(value == null ? '' : value).replace(/\D/g, '')
      if (!text) return ''
      return String(Math.min(parseInt(text, 10), 100))
    },
    syncPaymentRates(value) {
      // 尾款固定为 100% 减首款，保证两项合计不超过 100%。
      const first = this.normalizeRate(value)
      this.$set(this.detail.buy_channel, 'part_pay_first_rate', first)
      this.$set(this.detail.buy_channel, 'part_pay_last_rate', first === '' ? '' : String(100 - Number(first)))
    },
    handleFirstRateInput(value) {
      // 输入过程中即时同步尾款，避免出现可编辑的尾款值。
      this.syncPaymentRates(value)
    },
    handlePayTypeChange() {
      // 切换到分批付款时，仅根据已有首款计算尾款，不覆盖接口回填的首款值。
      if (this.detail.buy_channel.pay_type == 2) {
        const first = this.normalizeRate(this.detail.buy_channel.part_pay_first_rate)
        this.$set(this.detail.buy_channel, 'part_pay_last_rate', first === '' ? '' : String(100 - Number(first)))
      }
      return this.saveField('pay_type')
    },
    savePaymentRates() {
      // 失焦时统一保存首款和自动计算后的尾款。
      this.syncPaymentRates(this.detail.buy_channel.part_pay_first_rate)
      return Promise.all([
        this.saveField('part_pay_first_rate'),
        this.saveField('part_pay_last_rate')
      ])
    },
    preventInvalidRateKey(event) {
      // 禁止负号、小数点和字母，只保留编辑控制键与数字键。
      if (event.ctrlKey || event.metaKey || ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return
      if (!/^\d$/.test(event.key)) event.preventDefault()
    },
    preventInvalidRatePaste(event) {
      // 粘贴内容必须全部由数字组成，范围限制交由输入处理逻辑完成。
      if (!/^\d*$/.test(event.clipboardData.getData('text'))) event.preventDefault()
    },
    deleteContract() {
      // 删除合同文件时清空 URL 和文件名，并同步提交两个字段。
      this.$set(this.detail.buy_channel, 'file', '')
      this.$set(this.detail.buy_channel, 'file_name', '')
      return Promise.all([
        this.saveField('file'),
        this.saveField('file_name')
      ])
    },
    // 上传合同文件后保存接口返回的 URL 和本地文件名。
    async uploadContract(file) {
      const result = await this.createClient(file)
      this.$set(this.detail.buy_channel, 'file', result.data)
      this.$set(this.detail.buy_channel, 'file_name', file.file.name)
      await Promise.all([this.saveField('file'), this.saveField('file_name')])
    }
  }
}
</script>

<style lang="scss" scoped>
.purchase-shell { margin-top: 24px; padding: 24px; border-radius: 8px; background: #f8f8f8; }
h2 { margin: 0 0 24px; font-size: 24px; line-height: 32px; }
h3 { margin: 0 0 24px; font-size: 16px; line-height: 22px; }
.channel-radio { margin-bottom: 24px; }
.purchase-grid { display: grid; grid-template-columns: 300px 296px 544px; gap: 30px; }
.purchase-card { box-sizing: border-box; min-height: 232px; padding: 18px; background: #fff; }
.rate-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-top: 28px; }
.rate-row .el-input { margin-top: 8px; }
.purchase-card > .el-upload,
.purchase-card > a { display: block; margin-top: 20px; }
.contract-card h3 { margin-bottom: 14px; }
.contract-file { display: flex; align-items: center; gap: 8px; min-width: 0; margin-top: 20px; }
.contract-file a { min-width: 0; overflow: hidden; color: #0d4fe6; text-overflow: ellipsis; white-space: nowrap; }
.delete-contract { flex: 0 0 auto; margin-left: auto; padding: 0; border: 0; background: transparent; color: #b4272d; font-size: 16px; cursor: pointer; }
.contract-card .contract-file { margin-top: 0; }
.contract-card > .contract-upload { display: block; margin-top: 12px; }
.contact-card label { display: grid; grid-template-columns: 100px 1fr 1fr; align-items: center; gap: 12px; margin-bottom: 14px; }
.blue-button { border-color: #2c96e7 !important; background: #2c96e7 !important; color: #fff !important; }
</style>
